import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { IconClose, IconEdit, IconEye } from './icons'
import { Markdown } from './chat/Markdown'
import { cn } from '../lib/cn'

export function countMarkdownStats(text: string): { chars: number; lines: number } {
  if (!text) return { chars: 0, lines: 0 }
  return { chars: text.length, lines: text.split('\n').length }
}

export function insertTabAtCursor(text: string, start: number, end: number): { text: string; newCursor: number } {
  const updated = text.slice(0, start) + '  ' + text.slice(end)
  return { text: updated, newCursor: start + 2 }
}

export interface MarkdownOverlayEditorProps {
  open: boolean
  value: string
  onChange(val: string): void
  onClose(): void
  title?: string
}

export function MarkdownOverlayEditor({
  open,
  value,
  onChange,
  onClose,
  title
}: MarkdownOverlayEditorProps) {
  const { t } = useTranslation()
  const [showPreview, setShowPreview] = useState(true)
  const [mobileTab, setMobileTab] = useState<'edit' | 'preview'>('edit')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)

    // Autofocus textarea when opened
    requestAnimationFrame(() => {
      textareaRef.current?.focus()
    })

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  const stats = countMarkdownStats(value)
  const targetContainer = typeof document !== 'undefined'
    ? (document.getElementById('app-content-col') ?? document.body)
    : null

  if (!targetContainer) return null

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault()
      const el = e.currentTarget
      const start = el.selectionStart
      const end = el.selectionEnd
      const { text, newCursor } = insertTabAtCursor(value, start, end)
      onChange(text)
      requestAnimationFrame(() => {
        el.selectionStart = newCursor
        el.selectionEnd = newCursor
      })
    }
  }

  const content = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ?? t('automations.editor.title')}
      className="absolute inset-0 z-30 flex flex-col bg-cream-50 shadow-sheet"
    >
      {/* Top Header Bar. The overlay covers the app's own top bar, so on a phone it has to clear the
          status bar / Dynamic Island itself. */}
      <div className="flex h-[calc(3.5rem+env(safe-area-inset-top,0px))] shrink-0 items-center justify-between border-b border-line bg-cream-50 px-[calc(1rem+env(safe-area-inset-left,0px))] pt-[env(safe-area-inset-top,0px)] sm:h-14 sm:px-6 sm:pt-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ginger-50 text-ginger-600">
            <IconEdit className="h-4 w-4" />
          </div>
          <div className="flex items-baseline gap-2 truncate">
            <h2 className="truncate font-display text-base font-semibold text-ink-100">
              {title ?? t('automations.editor.title')}
            </h2>
            <span className="hidden font-mono text-xs text-ink-500 sm:inline-block">
              {t('automations.editor.stats', {
                chars: stats.chars.toLocaleString(),
                lines: stats.lines.toLocaleString()
              })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Mobile Segmented Switcher (< 768px) */}
          <div className="flex rounded-lg bg-cream-200 p-0.5 md:hidden">
            <button
              type="button"
              onClick={() => setMobileTab('edit')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors',
                mobileTab === 'edit'
                  ? 'bg-cream-50 font-semibold text-ink-100'
                  : 'text-ink-400 hover:text-ink-200'
              )}
            >
              <IconEdit className="h-3.5 w-3.5" />
              <span>{t('automations.editor.tabEdit')}</span>
            </button>
            <button
              type="button"
              onClick={() => setMobileTab('preview')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors',
                mobileTab === 'preview'
                  ? 'bg-cream-50 font-semibold text-ink-100'
                  : 'text-ink-400 hover:text-ink-200'
              )}
            >
              <IconEye className="h-3.5 w-3.5" />
              <span>{t('automations.editor.tabPreview')}</span>
            </button>
          </div>

          {/* Desktop Live Preview Toggle (>= 768px) */}
          <button
            type="button"
            onClick={() => setShowPreview(prev => !prev)}
            className={cn(
              'hidden items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors md:inline-flex',
              showPreview
                ? 'border-ginger-400 bg-ginger-50 font-semibold text-ginger-600'
                : 'border-cream-300 bg-cream-50 text-ink-300 hover:bg-cream-200 hover:text-ink-100'
            )}
          >
            <IconEye className="h-3.5 w-3.5" />
            <span>
              {showPreview
                ? t('automations.editor.previewToggleOn')
                : t('automations.editor.previewToggleOff')}
            </span>
          </button>

          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            title={t('common.close')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-ink-100 px-3.5 py-1.5 text-xs font-medium text-canvas transition-colors hover:bg-ink-200 focus:outline-none focus:ring-2 focus:ring-ginger-500"
          >
            <span>{t('common.done')}</span>
            <span className="hidden opacity-60 sm:inline">(Esc)</span>
            <IconClose className="h-3.5 w-3.5 sm:hidden" />
          </button>
        </div>
      </div>

      {/* Main Split Body */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden bg-cream-100">
        {/* Editor Pane */}
        <div
          className={cn(
            'flex flex-col bg-cream-50 transition-all',
            // Mobile visibility
            mobileTab === 'edit' ? 'flex flex-1' : 'hidden md:flex',
            // Desktop width
            showPreview ? 'md:w-1/2 md:border-r md:border-line' : 'mx-auto w-full max-w-5xl'
          )}
        >
          <div className="flex h-9 shrink-0 items-center justify-between border-b border-line bg-cream-100 px-4 text-xs font-medium text-ink-400">
            <span>{t('automations.editor.rawPaneTitle')}</span>
            <span className="hidden font-mono text-[11px] text-ink-500 sm:inline">
              {t('automations.editor.rawPaneHint')}
            </span>
          </div>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder={t('automations.instructionsHint')}
            className="flex-1 w-full resize-none px-6 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] font-sans text-sm leading-relaxed text-ink-100 placeholder:text-ink-400 bg-transparent outline-none overflow-y-auto"
          />
        </div>

        {/* Live Preview Pane */}
        <div
          className={cn(
            'flex flex-col bg-cream-50 overflow-hidden',
            // Mobile visibility
            mobileTab === 'preview' ? 'flex flex-1' : 'hidden',
            // Desktop visibility
            showPreview ? 'md:flex md:w-1/2' : 'md:hidden'
          )}
        >
          <div className="flex h-9 shrink-0 items-center justify-between border-b border-line bg-cream-100 px-4 text-xs font-medium text-ink-400">
            <span>{t('automations.editor.previewPaneTitle')}</span>
            <span className="font-mono text-[11px] text-ginger-600 font-semibold">
              GFM
            </span>
          </div>
          <div className="flex-1 overflow-y-auto px-6 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]">
            {value.trim() ? (
              <div className="prose prose-sm max-w-none text-ink-200 leading-relaxed">
                <Markdown text={value} />
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-xs italic text-ink-400">
                {t('automations.editor.emptyPreview')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(content, targetContainer)
}
